import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './RulesTab.css';

interface RuleSet {
  rules: string;
}

interface Category {
  name: string;
  description: string;
  rule_sets: RuleSet[];
  requires_all: boolean;
  reasoning: string;
}

interface RulesData {
  system_prompt_preamble: string;
  updated_at: string;
  version: number;
  rule_id: string;
  created_at: string;
  categories: Category[];
}

const RulesTab: React.FC = () => {
  const [rulesData, setRulesData] = useState<RulesData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Editing states
  const [editableCategories, setEditableCategories] = useState<Category[] | null>(null);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState<number>(0);
  const [savingCategory, setSavingCategory] = useState<{ [key: number]: boolean }>({});
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchRules = async () => {
      try {
        const response = await axios.get('/rules');
        setRulesData(response.data);
        setEditableCategories(response.data.categories ? [...response.data.categories] : []);
        // Initialize draft textarea contents from incoming rule_sets (preserve newlines and spaces)
        setDraftRules(response.data.categories ? response.data.categories.map((c: Category) => c.rule_sets.map(rs => stripBulletsToText(rs.rules))) : []);
        setLoading(false);
      } catch (err) {
        console.warn('Error fetching rules:', err);
        setError('Failed to load rules data. Please try again later.');
        setLoading(false);
      }
    };

    fetchRules();
  }, []);

  // Convert stored bullet lines ("- line") into plain textarea content (preserve spaces and blank lines)
  const stripBulletsToText = (rules: string) => {
    if (!rules) return '';
    return rules.split('\n').map(line => line.replace(/^-\s?/, '')).join('\n');
  };

  // Convert textarea plain text into stored bullet format. Trim each line's ends but keep internal spaces. Empty/whitespace-only lines are ignored when saving.
  const bulletsFromText = (text: string) => {
    if (!text) return '';
    const lines = text.split('\n').map(l => l.trim());
    const filtered = lines.filter(l => l !== '');
    if (filtered.length === 0) return '';
    return filtered.map(l => `- ${l}`).join('\n');
  };

  const parseRules = (rules: string) => {
    if (!rules) return [''];
    return rules.split('\n').map(r => r.replace(/^- /, '').trim());
  };

  const joinRules = (arr: string[]) => {
    // If only empty string, return ''
    const filtered = arr.map(r => r.trim());
    if (filtered.length === 0) return '';
    // If all items empty -> return empty string
    if (filtered.every(r => r === '')) return '';
    // Prefix each line with '- '
    return filtered.filter(r => r !== '').map(r => `- ${r}`).join('\n');
  };






  // Draft text buffers for each rule_set textarea so typing newlines/spaces are preserved while editing
  const [draftRules, setDraftRules] = useState<string[][] | null>(null);

  const handleDraftChange = (catIndex: number, rsIndex: number, value: string) => {
    setDraftRules(prev => {
      const copy = prev ? prev.map(arr => [...arr]) : [];
      while (copy.length <= catIndex) copy.push([]);
      while (copy[catIndex].length <= rsIndex) copy[catIndex].push('');
      copy[catIndex][rsIndex] = value;
      return copy;
    });
  };

  const updateRuleText = (catIndex: number, setIndex: number, ruleIndex: number, value: string) => {
    // kept for backward compatibility but not used in the new textarea-based editor
    if (!editableCategories) return;
    const categories = editableCategories.map((cat) => ({ ...cat, rule_sets: cat.rule_sets.map(rs => ({ ...rs })) }));
    const arr = parseRules(categories[catIndex].rule_sets[setIndex].rules);
    while (arr.length <= ruleIndex) arr.push('');
    arr[ruleIndex] = value;
    categories[catIndex].rule_sets[setIndex].rules = joinRules(arr);
    setEditableCategories(categories);
  };

  const addRule = (catIndex: number, setIndex: number) => {
    // not used in textarea editor
    if (!editableCategories) return;
    const categories = editableCategories.map((cat) => ({ ...cat, rule_sets: cat.rule_sets.map(rs => ({ ...rs })) }));
    const arr = parseRules(categories[catIndex].rule_sets[setIndex].rules);
    arr.push('');
    categories[catIndex].rule_sets[setIndex].rules = joinRules(arr);
    setEditableCategories(categories);
  };

  const deleteRule = (catIndex: number, setIndex: number, ruleIndex: number) => {
    // not used in textarea editor
    if (!editableCategories) return;
    const categories = editableCategories.map((cat) => ({ ...cat, rule_sets: cat.rule_sets.map(rs => ({ ...rs })) }));
    const arr = parseRules(categories[catIndex].rule_sets[setIndex].rules);
    arr.splice(ruleIndex, 1);
    if (arr.length === 0) {
      // Remove the entire rule set if it's empty after deletion
      categories[catIndex].rule_sets.splice(setIndex, 1);
      // If no rule sets remain, keep a single empty set per requirement
      if (categories[catIndex].rule_sets.length === 0) {
        categories[catIndex].rule_sets.push({ rules: '' });
      }
    } else {
      categories[catIndex].rule_sets[setIndex].rules = joinRules(arr);
    }
    setEditableCategories(categories);
  };

  const addRuleSet = (catIndex: number) => {
    if (!editableCategories) return;
    const categories = editableCategories.map((cat) => ({ ...cat, rule_sets: cat.rule_sets.map(rs => ({ ...rs })) }));
    categories[catIndex].rule_sets.push({ rules: '' });
    setEditableCategories(categories);
    // ensure draftRules has a corresponding empty textarea
    setDraftRules(prev => {
      const copy = prev ? prev.map(arr => [...arr]) : [];
      while (copy.length <= catIndex) copy.push([]);
      copy[catIndex].push('');
      return copy;
    });
  };

  const deleteRuleSet = (catIndex: number, setIndex: number) => {
    if (!editableCategories) return;
    const categories = editableCategories.map((cat) => ({ ...cat, rule_sets: cat.rule_sets.map(rs => ({ ...rs })) }));
    categories[catIndex].rule_sets.splice(setIndex, 1);
    if (categories[catIndex].rule_sets.length === 0) categories[catIndex].rule_sets.push({ rules: '' });
    setEditableCategories(categories);
    // also remove draft entry
    setDraftRules(prev => {
      const copy = prev ? prev.map(arr => [...arr]) : [];
      if (copy[catIndex]) {
        copy[catIndex].splice(setIndex, 1);
        if (copy[catIndex].length === 0) copy[catIndex].push('');
      }
      return copy;
    });
  };

  const saveCategory = async (catIndex: number) => {
    if (!rulesData || !editableCategories || !draftRules) return;
    setSavingCategory(prev => ({ ...prev, [catIndex]: true }));
    setMessage(null);
    try {
      // Build new rule_sets for the target category using the draft textarea contents
      const categories = editableCategories.map(cat => ({ ...cat, rule_sets: cat.rule_sets.map(rs => ({ ...rs })) }));
      const targetCat = { ...categories[catIndex] };

      const newSets = (draftRules[catIndex] || []).map(text => ({ rules: bulletsFromText(text) }));
      const filteredSets = newSets.filter(rs => rs.rules && rs.rules.trim() !== '');

      if (filteredSets.length === 0) {
        // If user removed all sets or left them empty, keep a single empty set as required
        targetCat.rule_sets = [{ rules: '' }];
      } else {
        targetCat.rule_sets = filteredSets;
      }

      categories[catIndex] = targetCat;

      const payload: RulesData = { ...rulesData, categories };
      await axios.put('/rules', payload);

      // Update local copies and reset drafts to reflect saved data
      setRulesData(payload);
      setEditableCategories(categories);
      setDraftRules(payload.categories.map((c: Category) => c.rule_sets.map(rs => stripBulletsToText(rs.rules))));
      setMessage('Category saved successfully.');
    } catch (err) {
      console.warn('Failed to save category:', err);
      setMessage('Failed to save category.');
    } finally {
      setSavingCategory(prev => ({ ...prev, [catIndex]: false }));
      setTimeout(() => setMessage(null), 3000);
    }
  };

  if (loading) {
    return (
      <div className="rules-loading">
        <div className="spinner"></div>
        <p>Loading business rules...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rules-error">
        <div className="error-icon">⚠️</div>
        <p>{error}</p>
        <button onClick={() => window.location.reload()} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (!rulesData) return null;

  return (
    <div className="rules-container">
      <div className="rules-header">
        <h2>Business Rules Configuration</h2>
        <div className="rules-meta">
          <span className="version-badge">Version {rulesData.version}</span>
          <span className="updated-date">
            Updated: {new Date(rulesData.updated_at).toLocaleDateString()}
          </span>
        </div>
      </div>



      <div className="categories-grid">
        {rulesData.categories.map((category, index) => (
          <div key={index} className={`category-card ${category.name.toLowerCase()}`}>
            <div className="category-header">
              <h3>{category.name}</h3>
              <p className="category-description">{category.description}</p>
            </div>

            <div className="category-body">
              <div className="category-reasoning">
                <h4>Reasoning</h4>
                <div className="reasoning-wrapper" tabIndex={0} aria-label={category.reasoning}>
                  <p className="reasoning-text">{category.reasoning}</p>
                  <div className="reasoning-tooltip" role="tooltip">{category.reasoning}</div>
                </div>
              </div>

              <div className="rule-sets">
                <h4>Rule Sets {category.requires_all ? '(All Required)' : '(Any Match)'}</h4>

                {/* Read-only view */}
                {category.rule_sets.map((ruleSet, rsIndex) => (
                  <div key={rsIndex} className="rule-set">
                    <ul>
                      {ruleSet.rules.split('\n').map((rule, rIndex) => (
                        <li key={rIndex}>{rule.replace(/^- /, '')}</li>
                      ))}
                    </ul>
                  </div>
                ))}

              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Editor block for selecting and editing a category */}
      <div className="system-prompt-section">
        <h3>Edit Rules by Category</h3>
        <div className="category-select-wrapper" style={{ marginBottom: 12 }}>
          <label className="category-select-label">Select Category</label>
          <select
            className="category-select"
            value={selectedCategoryIndex}
            onChange={(e) => setSelectedCategoryIndex(Number(e.target.value))}
          >
            {rulesData.categories.map((c, i) => (
              <option key={i} value={i}>{c.name}</option>
            ))}
          </select>
        </div> 

        {/* Editor for selected category */}
        {editableCategories && editableCategories[selectedCategoryIndex] && (
          <div className="category-edit-panel">
            {/* <h4 style={{ marginTop: 0 }}>Editing: {editableCategories[selectedCategoryIndex].name}</h4> */}

            {editableCategories[selectedCategoryIndex].rule_sets.map((ruleSet, rsIndex) => (
              <div key={rsIndex} className="rule-set editable">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <strong>Rule Set {rsIndex + 1}</strong>
                  <button
                    className="delete-set-icon"
                    aria-label={`Delete rule set ${rsIndex + 1}`}
                    onClick={() => deleteRuleSet(selectedCategoryIndex, rsIndex)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>

                <div style={{ marginTop: 8 }}>
                  <textarea
                    className="rule-textarea with-delete-icon"
                    value={draftRules && draftRules[selectedCategoryIndex] ? draftRules[selectedCategoryIndex][rsIndex] : stripBulletsToText(ruleSet.rules)}
                    onChange={(e) => handleDraftChange(selectedCategoryIndex, rsIndex, e.target.value)}
                    rows={6}
                  />
                </div>
              </div>
            ))}

            <div className="edit-actions" style={{ marginTop: 12 }}>
              <div>
                <button className="btn btn-secondary" onClick={() => addRuleSet(selectedCategoryIndex)}>+ Add Rule Set</button>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" onClick={() => {
                  // cancel: revert changes to original rulesData and draft text
                  setEditableCategories(rulesData ? [...rulesData.categories] : []);
                  setDraftRules(rulesData ? rulesData.categories.map((c: Category) => c.rule_sets.map(rs => stripBulletsToText(rs.rules))) : []);
                  setMessage(null);
                }}>Cancel</button>

                <button className="btn btn-primary" onClick={() => saveCategory(selectedCategoryIndex)} disabled={!!savingCategory[selectedCategoryIndex]}>
                  {savingCategory[selectedCategoryIndex] ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            {message && <div className="update-message">{message}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default RulesTab;
